import React, { useEffect, useRef, useState } from "react";
import { Calendar, Clock, Mail, MapPin, Phone, Send, X } from "lucide-react";
import AuthenticityTokenField from "../../../components/forms/AuthenticityTokenField";
import { OptionalPhotoUploadField } from "../../../components/forms/OptionalPhotoUploadField";
import { Button } from "../../../components/ui/button";
import {
  FieldLabel,
  TextAreaInput,
  TextInput,
} from "../../../components/ui/FormControls";
import { SectionHeader } from "../../../components/ui/section";
import { cn } from "../../../lib/utils";
import {
  CONTRACTOR_SURFACE_QUOTE_SHELL_CLASS,
  CONTRACTOR_SURFACE_RADIUS_LG_CLASS,
  CONTRACTOR_SURFACE_RADIUS_MD_CLASS,
} from "../layoutRadius";
import type { ContractorSitePageProps } from "../types";

type TimeSlot = {
  start: string;
  end: string;
  display: string;
};

type QuoteSectionProps = Pick<
  ContractorSitePageProps,
  "csrfToken" | "contactPath" | "business"
>;

export const QuoteSection: React.FC<QuoteSectionProps> = ({
  csrfToken,
  contactPath,
  business,
}) => {
  const [availableSlots, setAvailableSlots] = useState<TimeSlot[]>([]);
  const [selectedSlots, setSelectedSlots] = useState<string[]>([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedService, setSelectedService] = useState("");
  const formRef = useRef<HTMLFormElement>(null);

  // Read service from URL hash (e.g., #contact?service=Kitchen%20Remodeling)
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash;
      if (hash.startsWith("#contact?service=")) {
        const serviceName = decodeURIComponent(
          hash.replace("#contact?service=", ""),
        );
        // Verify service exists in business services
        if (business.services.some((s) => s.name === serviceName)) {
          setSelectedService(serviceName);
          // Scroll to contact section since browser can't find the element with query params
          document
            .getElementById("contact")
            ?.scrollIntoView({ behavior: "smooth" });
        }
      }
    };

    handleHashChange(); // Check on mount
    window.addEventListener("hashchange", handleHashChange);
    return () => window.removeEventListener("hashchange", handleHashChange);
  }, [business.services]);

  useEffect(() => {
    if (business.availabilityEnabled && business.id) {
      loadAvailableSlots();
    }
  }, [business.availabilityEnabled, business.id]);

  const loadAvailableSlots = async () => {
    if (!business.id) return;

    setLoadingSlots(true);
    const url = `/availability/slots?business_id=${business.id}`;

    try {
      const response = await fetch(url);
      const data = await response.json();
      setAvailableSlots(data.slots || []);
    } catch (error) {
      console.error("Failed to load slots:", error);
    } finally {
      setLoadingSlots(false);
    }
  };

  const toggleSlot = (slotStart: string) => {
    setSelectedSlots((prev) => {
      if (prev.includes(slotStart)) {
        return prev.filter((s) => s !== slotStart);
      }
      if (prev.length >= 3) {
        return prev; // Max 3 selections
      }
      return [...prev, slotStart];
    });
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    // If availability is enabled and slots are available, show modal instead of submitting
    if (business.availabilityEnabled && availableSlots.length > 0) {
      e.preventDefault();
      // Validate form first
      if (formRef.current?.checkValidity()) {
        setShowModal(true);
      } else {
        formRef.current?.reportValidity();
      }
    }
  };

  const handleModalSubmit = () => {
    // Submit the form with selected time preferences
    formRef.current?.submit();
  };

  const handleSkipTimes = () => {
    // Clear selections and submit without time preferences
    setSelectedSlots([]);
    setShowModal(false);
    // Need a small delay to clear the hidden inputs before submitting
    setTimeout(() => {
      formRef.current?.submit();
    }, 0);
  };

  return (
    <>
      <section id="contact" className="bg-background py-20 sm:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-12 lg:grid-cols-5 lg:gap-16">
            <div className="lg:col-span-2">
              <SectionHeader
                className="mb-10"
                kicker="Contact Us"
                title="Get your free project estimate"
                description="Tell us what you need and we will follow up with scope and next steps."
              />

              <div className="space-y-5">
                <div className="flex items-start gap-4">
                  <div
                    className={cn(
                      "flex h-10 w-10 shrink-0 items-center justify-center bg-muted",
                      CONTRACTOR_SURFACE_RADIUS_MD_CLASS,
                    )}
                  >
                    <Phone className="h-5 w-5 text-accent" />
                  </div>
                  <div>
                    <div className="text-sm font-medium text-foreground">
                      {business.phone}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Call us directly
                    </div>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div
                    className={cn(
                      "flex h-10 w-10 shrink-0 items-center justify-center bg-muted",
                      CONTRACTOR_SURFACE_RADIUS_MD_CLASS,
                    )}
                  >
                    <Mail className="h-5 w-5 text-accent" />
                  </div>
                  <div>
                    <a
                      href={`mailto:${business.businessEmail}`}
                      className="text-sm font-medium text-foreground hover:underline"
                    >
                      {business.businessEmail}
                    </a>
                    <div className="text-xs text-muted-foreground">
                      Email us anytime
                    </div>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div
                    className={cn(
                      "flex h-10 w-10 shrink-0 items-center justify-center bg-muted",
                      CONTRACTOR_SURFACE_RADIUS_MD_CLASS,
                    )}
                  >
                    <MapPin className="h-5 w-5 text-accent" />
                  </div>
                  <div>
                    <div className="text-sm font-medium text-foreground">
                      {business.serviceArea}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Primary service region
                    </div>
                  </div>
                </div>
                {business.businessHours.length > 0 ? (
                  <div className="flex items-start gap-4">
                    <div
                      className={cn(
                        "flex h-10 w-10 shrink-0 items-center justify-center bg-muted",
                        CONTRACTOR_SURFACE_RADIUS_MD_CLASS,
                      )}
                    >
                      <Clock className="h-5 w-5 text-accent" />
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground">
                        {business.businessHours[0]}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        Business hours
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
            </div>

            <div className="lg:col-span-3">
              <div
                className={cn(
                  "border border-border/70 bg-card p-6 text-card-foreground shadow-card sm:p-8 lg:p-10",
                  CONTRACTOR_SURFACE_QUOTE_SHELL_CLASS,
                )}
              >
                <form
                  ref={formRef}
                  action={contactPath}
                  method="post"
                  encType="multipart/form-data"
                  className="space-y-5"
                  onSubmit={handleFormSubmit}
                >
                  <AuthenticityTokenField csrfToken={csrfToken} />
                  <h3 className="mb-1 font-display text-2xl font-semibold text-foreground">
                    Request a Quote
                  </h3>
                  <p className="mb-6 mt-0! text-sm text-muted-foreground">
                    Fill in your details and we will respond promptly.
                  </p>

                  <div className="grid gap-5 sm:grid-cols-2">
                    <div>
                      <FieldLabel htmlFor="quote_name">Full Name *</FieldLabel>
                      <TextInput
                        id="quote_name"
                        name="quote[name]"
                        type="text"
                        required
                        placeholder="John Smith"
                        className={cn(
                          "py-3.5",
                          CONTRACTOR_SURFACE_RADIUS_MD_CLASS,
                        )}
                      />
                    </div>
                    <div>
                      <FieldLabel htmlFor="quote_email">
                        Email Address *
                      </FieldLabel>
                      <TextInput
                        id="quote_email"
                        name="quote[email]"
                        type="email"
                        required
                        placeholder="john@email.com"
                        className={cn(
                          "py-3.5",
                          CONTRACTOR_SURFACE_RADIUS_MD_CLASS,
                        )}
                      />
                    </div>
                  </div>

                  <div className="grid gap-5 sm:grid-cols-2">
                    <div>
                      <FieldLabel htmlFor="quote_phone">
                        Phone Number
                      </FieldLabel>
                      <TextInput
                        id="quote_phone"
                        name="quote[phone]"
                        type="tel"
                        placeholder="(555) 000-0000"
                        className={cn(
                          "py-3.5",
                          CONTRACTOR_SURFACE_RADIUS_MD_CLASS,
                        )}
                      />
                    </div>
                    <div>
                      <FieldLabel htmlFor="quote_postal_code">
                        Postal Code *
                      </FieldLabel>
                      <TextInput
                        id="quote_postal_code"
                        name="quote[postal_code]"
                        type="text"
                        required
                        placeholder="M5V 1A1"
                        className={cn(
                          "py-3.5",
                          CONTRACTOR_SURFACE_RADIUS_MD_CLASS,
                        )}
                      />
                    </div>
                  </div>

                  <div>
                    <FieldLabel htmlFor="quote_service">
                      Service Needed *
                    </FieldLabel>
                    <select
                      id="quote_service"
                      name="quote[service]"
                      required
                      value={selectedService}
                      onChange={(e) => setSelectedService(e.target.value)}
                      className={cn(
                        "ds-native-select w-full border border-input bg-card px-4 py-3.5 text-card-foreground placeholder:text-muted-foreground transition-all focus:border-ring focus:outline-none focus:ring-2 focus:ring-ring/20 [--ds-select-chevron-inset:1rem]",
                        CONTRACTOR_SURFACE_RADIUS_MD_CLASS,
                      )}
                    >
                      <option value="">Select a service</option>
                      {business.services.map((service) => (
                        <option key={service.name} value={service.name}>
                          {service.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <FieldLabel htmlFor="quote_message">
                      Project Details *
                    </FieldLabel>
                    <TextAreaInput
                      id="quote_message"
                      name="quote[message]"
                      rows={4}
                      className={cn(
                        "resize-none",
                        CONTRACTOR_SURFACE_RADIUS_MD_CLASS,
                      )}
                      required
                      placeholder="Tell us about your project."
                    />
                  </div>

                  <OptionalPhotoUploadField
                    id="quote_photos"
                    name="quote[photos][]"
                    inputClassName={CONTRACTOR_SURFACE_RADIUS_MD_CLASS}
                  />

                  {/* Hidden inputs for selected time slots */}
                  {selectedSlots.map((slot) => (
                    <input
                      key={slot}
                      type="hidden"
                      name="time_preferences[]"
                      value={slot}
                    />
                  ))}

                  <Button
                    type="submit"
                    variant="default"
                    className={cn(
                      "w-full py-4 text-base",
                      CONTRACTOR_SURFACE_RADIUS_MD_CLASS,
                    )}
                    disabled={loadingSlots}
                  >
                    <Send className="h-4 w-4" />
                    Send Quote Request
                  </Button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Time Selection Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <div
            className="ds-overlay-backdrop absolute inset-0 backdrop-blur-sm"
            onClick={() => setShowModal(false)}
          />

          {/* Modal */}
          <div
            className={cn(
              "ds-overlay-card relative w-full max-w-lg p-6 sm:p-8",
              CONTRACTOR_SURFACE_RADIUS_LG_CLASS,
            )}
          >
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => setShowModal(false)}
              className="absolute right-4 top-4 rounded-full p-1 text-muted-foreground"
            >
              <X className="h-5 w-5" />
            </Button>

            <div className="mb-6 text-center">
              <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-accent/15">
                <Calendar className="h-6 w-6 text-accent" />
              </div>
              <h3 className="text-xl font-semibold text-foreground">
                Schedule a Quote Visit
              </h3>
              <p className="mt-2 text-sm text-muted-foreground">
                Select up to 3 preferred times for an in-person quote. This
                helps us serve you faster.
              </p>
            </div>

            <div className="mb-6 grid grid-cols-2 gap-2 max-h-64 overflow-y-auto">
              {availableSlots.map((slot) => (
                <Button
                  key={slot.start}
                  type="button"
                  variant="outline"
                  onClick={() => toggleSlot(slot.start)}
                  className={cn(
                    "h-auto justify-start p-3 text-left text-sm",
                    CONTRACTOR_SURFACE_RADIUS_MD_CLASS,
                    selectedSlots.includes(slot.start)
                      ? "border-accent bg-accent text-accent-foreground shadow-sm hover:bg-accent"
                      : "border-border bg-muted/35 hover:border-accent/60 hover:bg-accent/10",
                  )}
                >
                  {slot.display}
                </Button>
              ))}
            </div>

            {selectedSlots.length > 0 && (
              <p className="mb-4 text-center text-sm font-medium text-accent">
                {selectedSlots.length} time{selectedSlots.length > 1 ? "s" : ""}{" "}
                selected
              </p>
            )}

            <div className="flex flex-col gap-3 sm:flex-row">
              <Button
                type="button"
                variant="default"
                className={cn(
                  "flex-1 py-3",
                  CONTRACTOR_SURFACE_RADIUS_MD_CLASS,
                )}
                onClick={handleModalSubmit}
              >
                <Send className="h-4 w-4" />
                {selectedSlots.length > 0
                  ? "Submit Request"
                  : "Submit Without Times"}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={handleSkipTimes}
                className={cn(
                  "px-4 py-3 text-sm font-medium text-muted-foreground",
                  CONTRACTOR_SURFACE_RADIUS_MD_CLASS,
                )}
              >
                Skip
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
